import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Modal,
  Button,
  Radio,
  DatePicker,
  TimePicker,
  Space,
  message,
} from "antd";
import { BellOutlined } from "@ant-design/icons";
import "./Reminder.css";

dayjs.extend(utc);

const Reminder = ({
  show,
  handleClose,
  onSelectReminder,
  eventDate,
  eventTime,
  selectReminderTime,
  initialReminder,
  eventData,
}) => {
  const eventStartTime = eventTime?.start || null;

  const [selectedReminder, setSelectedReminder] = useState("30 minute before");
  const [customReminderDateTime, setCustomReminderDateTime] = useState(null);
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [customReminderDate, setCustomReminderDate] = useState(null);
  const [customReminderTime, setCustomReminderTime] = useState(null);

  const reminders = [
    "No reminder",
    "5 minute before",
    "30 minute before",
    "1 hour before",
    "1 day before",
    "Customize...",
  ];

  // merge date + time when either changes
  useEffect(() => {
    if (customReminderDate && customReminderTime) {
      const merged = customReminderDate
        .hour(customReminderTime.hour())
        .minute(customReminderTime.minute());
      setCustomReminderDateTime(merged);
    } else if (customReminderDate) {
      setCustomReminderDateTime(customReminderDate);
    } else {
      setCustomReminderDateTime(null);
    }
  }, [customReminderDate, customReminderTime]);

  // initialize reminder selection
  useEffect(() => {
    if (initialReminder) {
      if (initialReminder.includes("Customize")) {
        setIsCustomizing(true);
        if (eventData?.reminder) {
          const reminderDayjs = dayjs(eventData.reminder);
          setCustomReminderDateTime(reminderDayjs);
          setCustomReminderDate(reminderDayjs);
          setCustomReminderTime(reminderDayjs);
        }
        setSelectedReminder("Customize...");
      } else {
        setSelectedReminder(initialReminder);
      }
    } else {
      setSelectedReminder("No reminder");
      setIsCustomizing(false);
    }
  }, [initialReminder, eventData]);

  const handleSave = () => {
    if (selectedReminder === "No reminder") {
      onSelectReminder(null);
      message.success("Reminder set successfully");
      handleClose();
      return;
    }

    if (!eventDate || !eventStartTime) {
      message.error("Event date or start time is missing.");
      return;
    }

    const formattedDate = dayjs(eventDate);
    const formattedTime = dayjs(eventStartTime, "HH:mm");

    if (!formattedDate.isValid() || !formattedTime.isValid()) {
      message.error("Invalid event date or start time format.");
      return;
    }

    const eventDateTime = dayjs(
      `${formattedDate.format("YYYY-MM-DD")} ${formattedTime.format("HH:mm")}`
    );

    if (!eventDateTime.isValid()) {
      message.error("Invalid event datetime.");
      return;
    }

    let reminderTime = eventDateTime;

    switch (selectedReminder) {
      case "5 minute before":
        reminderTime = eventDateTime.subtract(5, "minute");
        break;
      case "30 minute before":
        reminderTime = eventDateTime.subtract(30, "minute");
        break;
      case "1 hour before":
        reminderTime = eventDateTime.subtract(1, "hour");
        break;
      case "1 day before":
        reminderTime = eventDateTime.subtract(1, "day");
        break;
      case "Customize...":
        if (customReminderDateTime && customReminderDateTime.isValid()) {
          reminderTime = customReminderDateTime;
        } else {
          message.error("Please select a custom date & time.");
          return;
        }
        break;
      default:
        break;
    }

    if (reminderTime.isAfter(eventDateTime)) {
      message.error("Reminder time cannot be after the event date & time.");
      return;
    }

    console.log(
      "Reminder Time (local):",
      reminderTime.format("YYYY-MM-DD HH:mm")
    );
    console.log(
      "Reminder Time (UTC):",
      reminderTime.utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
    );

    onSelectReminder(reminderTime.format("YYYY-MM-DDTHH:mm:ss[Z]"));

    message.success("Reminder set successfully");
    handleClose();
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <BellOutlined /> Reminder
        </div>
      }
      open={show}
      onCancel={() => {
        if (isCustomizing && !customReminderDateTime) {
          message.warning("Please select a custom date & time before closing.");
          return; // 🚫 block closing
        }
        handleClose(); // ✅ allow close
      }}
      footer={null}
      centered
      width={400}
      maskClosable={false}
    >
      <Radio.Group
        value={selectedReminder}
        onChange={(e) => {
          const value = e.target.value;
          setSelectedReminder(value);
          selectReminderTime(value);
          setIsCustomizing(value === "Customize...");
        }}
        style={{ display: "flex", flexDirection: "column", gap: "12px" }}
      >
        {reminders.map((reminder) => (
          <Radio key={reminder} value={reminder} className="radio-reminder">
            {reminder === "No reminder" ? "No Reminder" : reminder}
          </Radio>
        ))}
      </Radio.Group>

      {isCustomizing && (
        <Space
          direction="vertical"
          style={{ width: "100%", marginTop: "15px" }}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            value={customReminderDate}
            onChange={(date) => setCustomReminderDate(date)}
            disabledDate={(current) =>
              (current && current < dayjs().startOf("day")) ||
              (current && current.isAfter(dayjs(eventDate)))
            }
            placeholder="Select Date"
          />

          <TimePicker
            style={{ width: "100%" }}
            format="HH:mm"
            minuteStep={15}
            value={customReminderTime}
            getPopupContainer={(trigger) => trigger.parentNode}
            onChange={(time) => setCustomReminderTime(time)}
            disabledTime={() => {
              if (!customReminderDate || !eventStartTime) return {};
              return customReminderDate.isSame(dayjs(eventDate), "day")
                ? {
                    disabledHours: () =>
                      [...Array(24).keys()].filter(
                        (h) => h > dayjs(eventStartTime, "HH:mm").hour()
                      ),
                  }
                : {};
            }}
            placeholder="Select Time"
          />
        </Space>
      )}

      <Button
        type="primary"
        block
        style={{
          marginTop: "20px",
          backgroundColor: "#9B51E0",
          height: "45px",
          fontSize: "16px",
          borderRadius: "8px",
        }}
        onClick={handleSave}
        disabled={isCustomizing && !customReminderDateTime}
      >
        Save
      </Button>
    </Modal>
  );
};

export default Reminder;
